import { inject, Injectable, signal } from '@angular/core';
import { Api } from '../../core/constants/api.constants';
import { Collections } from '../../core/constants/collection.constants';
import { ApiService } from '../../core/services/api.service';
import { TurnModel, TurnsModel } from '../models/turn.model';
import { HttpParams } from '@angular/common/http';
import { TurnsStatisticsInterface } from '../interfaces/turn-statistics.interface';
import { Observable } from 'rxjs';
import { FilterMetadata, LazyLoadMeta, SortMeta } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class TurnService {
  private apiService = inject(ApiService);

  private path = Api.url.turn;
  public title = signal<string>('Turnos');
  public resource = Collections.TURN?.value.toLowerCase() as string;

  constructor() {}

  private getPath(path?: string): string {
    if (path) {
      return this.getPath() + '/' + path;
    }
    return this.path.base;
  }

  public get(id?: string, display?: string, servicio?: string) {
    const param: { [param: string]: any } = {};
    if (display) {
      param['display'] = display;
    }
    if (servicio) {
      param['servicio'] = servicio;
    }
    return this.apiService.get<TurnModel | TurnsModel>(this.getPath(id), {
      params: param,
    });
  }

  public getTurnStatistics(
    data: LazyLoadMeta
  ): Observable<TurnsStatisticsInterface> {
    // console.log('Turn statistics: ', data);
    const params: HttpParams = new HttpParams({
      fromObject: this.setParamsFromFilters(
        data.filters,
        this.setParamsFromSortMeta(data.multiSortMeta, {
          first: data.first ?? 0,
          rows: data.rows ?? 5,
        })
      ),
    });

    const path = this.getPath('statistics');
    console.log('params', params);
    // console.log('path', path);
    const r = this.apiService.post(path);
    return this.apiService.get<TurnsStatisticsInterface>(path, { params });
  }

  formatDates(dates?: Date[]) {
    if (!dates || dates.length === 0 || !dates[0] || !dates[1]) {
      return [];
    }
    const formattedDates = dates.map((date) => {
      return date?.toISOString().split('T')[0];
    });
    return formattedDates;
  }

  private setParamsFromFilters(
    filters?: {
      [s: string]: FilterMetadata | FilterMetadata[] | undefined;
    },
    params?: {
      [param: string]:
        | string
        | number
        | boolean
        | ReadonlyArray<string | number | boolean>;
    }
  ): {
    [param: string]:
      | string
      | number
      | boolean
      | ReadonlyArray<string | number | boolean>;
  } {
    // console.log(
    //   `[${TurnService.name}] Set Params: ${JSON.stringify(
    //     params
    //   )} From: ${JSON.stringify(filters)}`
    // );
    if (!params) {
      params = {};
    }
    if (!filters) {
      return params;
    }
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      // console.log(`[${TurnService.name}] Key: "${key}", Value: `, value);
      if (!value) {
        return;
      } else if (Array.isArray(value)) {
        params = this.setParamFromFilterArray(key, value, params);
        // console.log(
        //   `[${TurnService.name}] Key: "${key}", Value is Array, Params: `,
        //   params
        // );
      } else {
        params = this.setParamFromFilter(key, value, params);
        // console.log(
        //   `[${TurnService.name}] Key: "${key}", Value is NOT Array, Params: `,
        //   params
        // );
      }
    });
    return params;
  }

  private setParamFromFilter(
    key: string,
    filter?: FilterMetadata,
    params?: {
      [param: string]:
        | string
        | number
        | boolean
        | ReadonlyArray<string | number | boolean>;
    }
  ): {
    [param: string]:
      | string
      | number
      | boolean
      | ReadonlyArray<string | number | boolean>;
  } {
    if (!params) {
      params = {};
    }
    if (!filter || !filter.value) {
      return params;
    }
    if (Array.isArray(filter.value) && filter.value.length === 0) {
      return params;
    }
    params[key] = filter.value;
    return params;
  }
  private setParamFromFilterArray(
    key: string,
    filters?: FilterMetadata[],
    params?: {
      [param: string]:
        | string
        | number
        | boolean
        | ReadonlyArray<string | number | boolean>;
    }
  ): {
    [param: string]:
      | string
      | number
      | boolean
      | ReadonlyArray<string | number | boolean>;
  } {
    // console.log(
    //   `[${TurnService.name}] Set Param From Filter Array: `, params, `Key: ${key} From: `,
    //   filters
    // );
    if (!params) {
      params = {};
    }
    if (!filters || filters.length === 0) {
      return params;
    }
    params[key] = [];
    let count = 0;
    const values = filters
      .filter((filter) => {
        if (!filter || !filter.value) {
          return false;
        }
        if (Array.isArray(filter.value) && filter.value.length === 0) {
          return false;
        }
        return true;
      })
      .forEach((filter) => {
        // console.log(
        //   `[${TurnService.name}: ${count}] Set Param From Filter Array: `, params, `Key: ${key} From Filter: `,
        //   filter
        // );
        params[key] = [
          ...(params[key] as ReadonlyArray<
            string | number | boolean
          >),
          filter.value,
        ];
        count++;
      });
    return params;
  }

  setParamsFromSortMeta(
    sortMeta?: SortMeta[] | null,
    params?: {
      [param: string]:
        | string
        | number
        | boolean
        | ReadonlyArray<string | number | boolean>;
    }
  ): {
    [param: string]:
      | string
      | number
      | boolean
      | ReadonlyArray<string | number | boolean>;
  } {
    if (!params) {
      params = {};
    }
    if (!sortMeta || sortMeta.length === 0) {
      return params;
    }
    const sort: string[] = sortMeta
      .filter((value) => {
        if (
          !value ||
          !value.field ||
          !value.order ||
          typeof value.field !== 'string' ||
          typeof value.order !== 'number'
        ) {
          return false;
        }
        return true;
      })
      .map((value) => {
        return `${value.field}:${value.order}`;
      });
    params['sort'] = sort;
    return params;
  }

  getTurnStatisticsGraph(start: Date, end?: Date, contract?: string, branch?: string, service?: string): Observable<any> {
    const startDate: Date = new Date(start);
    // console.log('Start date: ', startDate);
    // startDate.setFullYear(2025, 2, 9);
    startDate.setHours(0, 0, 0, 0);
    // console.log('Start date change: ', startDate);
    const endDate: Date = new Date(end ? end : start);
    // console.log('End date: ', endDate);
    endDate.setHours(23, 0, 0, 0);
    // if (end) {
    //   endDate.setHours(endDate.getHours(), 0, 0, 0);
    // } else {
    // }
    const data: { [param: string]: any} = {
        startDate: startDate,
        endDate: endDate,
        contract: contract ? contract : '',
        branch: branch ? branch : '',
        service: service ? service : '',
      }
    // console.log('End date change: ', endDate);
    const params: HttpParams = new HttpParams({
      fromObject: data
    });
    // console.log('Params: ', params);
    // console.log('Path: ', this.getPath('statistics/graph'));
    return this.apiService.get<{
      data?: {
        hour?: number;
        date?: string;
        totalDuration?: number;
        count?: number;
        averageDurationMilliseconds?: number;
      }[];
      message: string;
      totalCount: number;
    }>(this.getPath('statistics/graph'), {
      params,
    });
  }
}
